import { Application, Texture, RenderTexture, Sprite } from "pixi.js";
import quantize from "quantize";
import type { ColorMap } from "quantize";
import { rgb_to_lab, diff, type LabColor, closest } from "color-diff";
import { sortFn } from "color-sorter";
import tinycolor from "tinycolor2";

function toLabs(colour: number): LabColor {
  const [r, g, b] = fromRgbHexValue(colour);
  return rgb_to_lab({ R: r, G: g, B: b });
}

/**
 * Sort colors so similar colors are grouped together.
 * Uses `color-sorter`'s opinionated hue→saturation order
 * (greys are moved to the end).
 */
export function sortColors2(colors: number[]): number[] {
  const toHex = (n: number) => `#${n.toString(16).padStart(6, "0")}`;
  const toNum = (s: string) => parseInt(s.slice(1), 16);

  const sortedHex = colors.map(toHex).sort(sortFn);
  return sortedHex.map(toNum);
}

export function sortColors(colorArray: number[]): number[] {
  const toHex = (n: number) => `#${n.toString(16).padStart(6, "0")}`;
  return colorArray.sort(
    (colorA, colorB) =>
      tinycolor(toHex(colorA)).toHsv().h - tinycolor(toHex(colorB)).toHsv().h
  );
}

function getThreshold(paletteSize: number): number {
  if (paletteSize <= 8) return 15;
  if (paletteSize <= 16) return 10;
  if (paletteSize <= 32) return 7;
  if (paletteSize <= 64) return 4;
  return 5;
}

export function dedupePalette(
  colors: number[],
  frequencies: number[],
  threshold: number = getThreshold(colors.length)
): number[] {
  const n = colors.length;
  if (n <= 1) return colors.slice();

  // guard: align frequencies length
  const w = (i: number) => Math.max(0, Number(frequencies[i] ?? 1)) || 1;

  const labs = colors.map(toLabs);

  // --- Union-Find (Disjoint Set) ---
  const parent = new Array(n).fill(0).map((_, i) => i);
  const find = (x: number): number => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]];
      x = parent[x];
    }
    return x;
  };
  const union = (a: number, b: number) => {
    const ra = find(a),
      rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  // connect pairs within threshold
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (diff(labs[i], labs[j]) < threshold) union(i, j);
    }
  }

  // collect clusters
  const groups = new Map<number, number[]>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    const g = groups.get(r);
    if (g) g.push(i);
    else groups.set(r, [i]);
  }

  // stable cluster order: earliest original index first
  const ordered = [...groups.values()].sort((a, b) => a[0] - b[0]);

  const result: number[] = [];

  for (const idxs of ordered) {
    if (idxs.length === 1) {
      result.push(colors[idxs[0]]);
      continue;
    }

    // pick weighted medoid
    let bestIdx = idxs[0];
    let bestScore = Infinity;

    for (const i of idxs) {
      let s = 0;
      for (const j of idxs) {
        if (i === j) continue;
        s += w(j) * diff(labs[i], labs[j]);
      }
      if (
        s < bestScore ||
        (s === bestScore &&
          (w(i) > w(bestIdx) || (w(i) === w(bestIdx) && i < bestIdx)))
      ) {
        bestScore = s;
        bestIdx = i;
      }
    }

    result.push(colors[bestIdx]);
  }

  return result;
}

/**
 * returns an rgb palette of K colours which are most suitable to colour in the texture
 * the rgb format are red, green, blue values from 0 - 1
 */
export const computePalette = async (
  app: Application,
  tex: Texture,
  K: number
): Promise<{ colorMap: ColorMap; frequencies: Array<number> } | undefined> => {
  if (!app || !tex) return undefined;
  K = Math.max(1, Math.floor(K));

  // -------- Downscale & read pixels --------
  // Use a modest target so clustering is fast but representative.
  const target = 192; // tweak 128..256 if you like
  const w0 = tex.width ?? tex.baseTexture?.width ?? 0;
  const h0 = tex.height ?? tex.baseTexture?.height ?? 0;
  if (!w0 || !h0) return undefined;

  const aspect = w0 / h0;
  const rw = Math.max(1, Math.round(aspect >= 1 ? target : target * aspect));
  const rh = Math.max(1, Math.round(aspect >= 1 ? target / aspect : target));

  const rt = RenderTexture.create({ width: rw, height: rh, resolution: 1 });
  const spr = new Sprite(tex);
  spr.width = rw;
  spr.height = rh;

  // Pixi v8-style render signature; works in v7+ with @types
  app.renderer.render({ container: spr, target: rt, clear: true });

  // Extract pixels (handle Pixi versions that return directly vs {pixels})
  const extracted = app.renderer.extract.pixels(rt);
  const data = extracted?.pixels ?? extracted;

  // Cleanup GL resources early
  spr.destroy();
  rt.destroy(true);

  if (!data || data.length < 4) return undefined;
  const pixels: Array<[number, number, number]> = [];
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a < 16) continue;
    pixels.push([data[i], data[i + 1], data[i + 2]]);
  }

  const cmap = quantize(pixels, K);
  if (!cmap) return undefined;

  const frequencies = histogram(pixels, cmap);
  return { colorMap: cmap, frequencies };
};

// returns a parallel array to cmap.palette() with frequency counts
function histogram(
  pixels: Array<[number, number, number]>,
  cmap: ColorMap
): Array<number> {
  const hist = new Map<number, number>(
    cmap.palette().map((rgb) => [toRgbHexValue(rgb), 0] as [number, number])
  );
  for (const [r, g, b] of pixels) {
    const c = toRgbHexValue(cmap.map([r, g, b]) as [number, number, number]);
    const prev = hist.get(c) ?? 0;
    hist.set(c, prev + 1);
  }
  return [...hist.values()];
}

export function nearestPaletteColor(c: number, palette: number[]): number {
  const rgbArr = fromRgbHexValue(c);
  const closestPalette = closest(
    {
      R: rgbArr[0],
      G: rgbArr[1],
      B: rgbArr[2],
    },
    palette.map((p) => {
      const rgbArr = fromRgbHexValue(p);
      return {
        R: rgbArr[0],
        G: rgbArr[1],
        B: rgbArr[2],
      };
    })
  );
  return toRgbHexValue([closestPalette.R, closestPalette.G, closestPalette.B]);
}

export const toRgbPalette = ([r, g, b]: [number, number, number]): [
  number,
  number,
  number
] => [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];

export const fromRgbPalette = ([r, g, b]: [number, number, number]): [
  number,
  number,
  number
] => [r / 255, g / 255, b / 255];

const clamp = (min: number, max: number, value: number): number => {
  return Math.min(max, Math.max(min, value));
};

/**
 * Convert from RGB tuple to a single int
 */
export const toRgbHexValue = ([r, g, b]: [number, number, number]): number => {
  return clamp(
    0,
    0xffffff,
    (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b)
  );
};

/**
 * Convert from RGB int to a tuple
 */
export const fromRgbHexValue = (value: number): [number, number, number] => {
  const r = (value >> 16) & 0xff;
  const g = (value >> 8) & 0xff;
  const b = value & 0xff;
  // return as rgb values 0 - 255
  return [r, g, b] as [number, number, number];
};
